import { useRef, useState } from 'react'
import styled from 'styled-components'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Avatar from 'components/Avatar'
import OwnerPicker from './OwnerPicker'
import { ownerFromAction } from './ownerDisplay'
import { BORDER, INK, MUTED } from './ui'

const OwnerCell = ({ action, onChange, variant = 'field' }) => {
  const row = variant === 'row'
  const btnRef = useRef(null)
  const [open, setOpen] = useState(false)
  const owner = ownerFromAction(action.owner)
  const editable = !!action.permissions?.canUpdate
  const pending = action.metadata?.imported?.peoplePending || []
  const standingIn = pending.length > 0 && !!action.ownerUserId && action.metadata?.imported?.defaultOwnerId === action.ownerUserId

  const content = owner ? (
    <>
      <Avatar firstName={owner.firstName} lastName={owner.lastName} email={owner.email} size={row ? 24 : 28} />
      <Name title={owner.name}>
        {row ? (
          <>
            <Full>{owner.name}</Full>
            <Short>{owner.firstName || owner.name}</Short>
          </>
        ) : owner.name}
      </Name>
      {!row && standingIn && <Muted>standing in for {pending.join(', ')}</Muted>}
    </>
  ) : pending.length > 0 ? (
    <Muted title='Owner in the previous system. They become the owner here once they are given access to this building.'>
      {pending.join(', ')} (not on Building Passport yet)
    </Muted>
  ) : (
    <Muted>No owner yet</Muted>
  )

  if (!editable) return <Static $row={row}>{content}</Static>

  return (
    <>
      <EditButton
        ref={btnRef}
        type='button'
        $row={row}
        aria-label={owner ? `Owner ${owner.name}. Change owner` : 'Choose an owner'}
        onClick={e => { e.stopPropagation(); setOpen(true) }}
      >
        {content}
        <EditHint style={{ fontSize: 16, color: MUTED }} aria-hidden='true' $row={row} />
      </EditButton>
      <OwnerPicker
        anchorEl={btnRef.current}
        open={open}
        onClose={() => setOpen(false)}
        buildingPassportId={action.buildingPassportId}
        currentOwnerId={action.ownerUserId}
        onSelect={person => {
          setOpen(false)
          if (person && person.id !== action.ownerUserId) onChange(action, person.id)
        }}
      />
    </>
  )
}

export default OwnerCell

const Static = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: ${({ $row }) => ($row ? 'nowrap' : 'wrap')};
  text-align: left;
  gap: 8px;
  min-height: 40px;
  max-width: 100%;
  padding: ${({ $row }) => ($row ? '0 8px' : '0')};
  font-size: ${({ $row }) => ($row ? '14px' : '15px')};
`

const EditHint = styled(EditOutlinedIcon)`
  flex-shrink: 0;
  ${({ $row }) => ($row ? 'opacity: 0; transition: opacity 0.12s ease;' : '')}
  @media (hover: none) { opacity: 1; }
`

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-wrap: ${({ $row }) => ($row ? 'nowrap' : 'wrap')};
  text-align: left;
  gap: 8px;
  min-height: 40px;
  max-width: 100%;
  padding: 4px 8px;
  border: 1px solid ${({ $row }) => ($row ? 'transparent' : BORDER)};
  border-radius: 8px;
  background: ${({ $row }) => ($row ? 'transparent' : '#fff')};
  font: inherit;
  font-size: ${({ $row }) => ($row ? '14px' : '15px')};
  text-align: left;
  cursor: pointer;
  &:hover { border-color: #a1a1aa; background: #fff; }
  &:hover ${EditHint}, &:focus-visible ${EditHint}, tr:hover & ${EditHint} { opacity: 1; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
`

const Name = styled.span`
  min-width: 24px;
  font-size: inherit;
  color: ${INK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Muted = styled.span`
  font-size: inherit;
  color: ${MUTED};
`

const Full = styled.span`
  @media (max-width: 768px) { display: none; }
`

const Short = styled.span`
  display: none;
  @media (max-width: 768px) { display: inline; }
`
